import { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

// ── Logging ────────────────────────────────────────────────────────────────
const log = {
  info:  (ctx, msg, data = {}) => console.log( `[Auth:${ctx}]`, msg, { ...data, t: new Date().toISOString() }),
  warn:  (ctx, msg, data = {}) => console.warn( `[Auth:${ctx}]`, msg, { ...data, t: new Date().toISOString() }),
  error: (ctx, err, data = {}) => console.error(`[Auth:${ctx}]`, {
    message: err?.message ?? String(err),
    status:  err?.status,
    code:    err?.code,
    ...data,
    t: new Date().toISOString(),
  }),
};

// ── Supabase error → human-readable message ───────────────────────────────
function friendlyError(err) {
  if (!err) return "Something went wrong.";
  const msg    = (err.message ?? "").toLowerCase();
  const status = err.status ?? 0;

  if (msg.includes("invalid login credentials"))         return "Incorrect email or password.";
  if (msg.includes("email not confirmed"))               return "Please verify your email address before signing in. Check your inbox.";
  if (msg.includes("user already registered"))           return "An account with this email already exists. Try signing in instead.";
  if (msg.includes("password should be at least"))       return "Password must be at least 6 characters.";
  if (msg.includes("signup is disabled"))                return "New registrations are temporarily disabled. Try again later.";
  if (msg.includes("over_email_send_rate_limit") ||
      msg.includes("rate limit"))                        return "Too many attempts. Please wait a minute and try again.";
  if (msg.includes("network"))                           return "Network error — check your connection and try again.";
  if (status === 429)                                    return "Too many requests. Please slow down and try again.";
  if (status === 422)                                    return "Invalid email or password format.";
  if (status === 400)                                    return "Invalid request — check your details and try again.";
  if (status === 500)                                    return "Server error. Please try again in a moment.";

  return err.message || "Something went wrong. Please try again.";
}

// ── Enrich session user with profile row ─────────────────────────────────
async function buildUser(sessionUser) {
  if (!sessionUser) return false;

  log.info("buildUser", "Fetching profile", { userId: sessionUser.id });

  try {
    const { data: profile, error } = await supabase
      .from("profiles")
      .select("name, plan, role")
      .eq("id", sessionUser.id)
      .single();

    if (error) {
      log.warn("buildUser", "Profile fetch failed — using session defaults", {
        error: error.message, code: error.code, userId: sessionUser.id,
      });
    } else {
      log.info("buildUser", "Profile loaded", { userId: sessionUser.id, plan: profile?.plan });
    }

    return {
      id:    sessionUser.id,
      email: sessionUser.email,
      name:  profile?.name  ?? sessionUser.user_metadata?.name ?? "",
      plan:  profile?.plan  ?? "free",
      role:  profile?.role  ?? "user",
    };
  } catch (err) {
    log.error("buildUser", err, { userId: sessionUser.id });
    return {
      id:    sessionUser.id,
      email: sessionUser.email,
      name:  sessionUser.user_metadata?.name ?? "",
      plan:  "free",
      role:  "user",
    };
  }
}

// ── Provider ──────────────────────────────────────────────────────────────
export function AuthProvider({ children }) {
  // null = loading  |  false = not signed in  |  object = signed in
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    log.info("init", "Bootstrapping auth");

    // ── Phase 1: fast initial session from localStorage (no network call)
    // getSession() resolves synchronously from the in-memory cache or
    // localStorage, so loading=false fires almost immediately.
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        log.error("getSession", error);
        setUser(false);
        setLoading(false);
        return;
      }

      if (session?.user) {
        // Set a basic user object immediately so pages can render
        const base = {
          id:    session.user.id,
          email: session.user.email,
          name:  session.user.user_metadata?.name ?? "",
          plan:  "free",
          role:  "user",
        };
        setUser(base);
        setLoading(false);
        log.info("getSession", "Session restored from cache", { userId: base.id });

        // Enrich with profile data in the background (non-blocking)
        buildUser(session.user)
          .then(setUser)
          .catch((err) => log.error("getSession/enrich", err));
      } else {
        setUser(false);
        setLoading(false);
        log.info("getSession", "No active session");
      }
    });

    // ── Phase 2: listen for subsequent auth state changes
    // Skip INITIAL_SESSION — handled above by getSession().
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "INITIAL_SESSION") return;

        log.info("onAuthStateChange", `Event: ${event}`, {
          hasSession: !!session,
          userId: session?.user?.id,
        });

        try {
          const enriched = await buildUser(session?.user ?? null);
          setUser(enriched);
        } catch (err) {
          log.error("onAuthStateChange", err, { event });
          setUser(false);
        } finally {
          setLoading(false);
        }
      },
    );

    return () => {
      log.info("init", "Unregistering auth listener");
      subscription.unsubscribe();
    };
  }, []);

  // ── login ────────────────────────────────────────────────────────────────
  const login = async (email, password) => {
    log.info("login", "Sign-in attempt", { email });
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      log.error("login", error, { email });
      throw new Error(friendlyError(error));
    }
    log.info("login", "Sign-in succeeded — setting user immediately", { email, userId: data.user.id });
    // Set a base user immediately from the response so PrivateRoute unblocks
    // without waiting for the SIGNED_IN auth event + buildUser() network call.
    // onAuthStateChange(SIGNED_IN) will still fire and enrich with plan/role.
    setUser({
      id:    data.user.id,
      email: data.user.email,
      name:  data.user.user_metadata?.name ?? "",
      plan:  "free",
      role:  "user",
    });
  };

  // ── register ─────────────────────────────────────────────────────────────
  const register = async (email, password, name) => {
    log.info("register", "Sign-up attempt", { email });
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    });
    if (error) {
      log.error("register", error, { email });
      throw new Error(friendlyError(error));
    }
    // If session is null after signup, Supabase requires email confirmation
    const confirmRequired = !data.session;
    log.info("register", "Sign-up succeeded", { email, confirmRequired });
    setUser(null);
    return { confirmRequired };
  };

  // ── logout ───────────────────────────────────────────────────────────────
  const logout = async () => {
    log.info("logout", "Signing out");
    const { error } = await supabase.auth.signOut();
    if (error) log.error("logout", error);
    setUser(false);
  };

  // ── refreshUser ──────────────────────────────────────────────────────────
  const refreshUser = async () => {
    log.info("refreshUser", "Refreshing profile");
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) { log.error("refreshUser", error); return; }
      if (session?.user) {
        const enriched = await buildUser(session.user);
        setUser(enriched);
        log.info("refreshUser", "Done", { userId: session.user.id });
      }
    } catch (err) {
      log.error("refreshUser", err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
